import React, { ForwardedRef } from 'react'
import styled from 'styled-components'

import { onClickEvent } from 'analytics/performance'
import { MenuButtonContent } from 'components/styled/menu/menu-item/menu-button-content'
import { useMenuContext } from 'components/styled/menu/menu-context'
import { ButtonEventProps } from 'components/styled/button/button'
import {
  BaseMenuButtonProps,
  BaseMenuItem,
  BaseMenuButtonStyle,
  BaseMenuButtonStyleProps,
} from './base-menu-button'

export interface MenuButtonProps extends ButtonEventProps, BaseMenuButtonProps {
  isSelected?: boolean
  isSubMenu?: boolean
  showSelectedIcon?: boolean
  'aria-describedby'?: string
}

export const StyledMenuButton = styled.button<BaseMenuButtonStyleProps>`
  ${BaseMenuButtonStyle};
`

export const MenuButton = React.forwardRef(
  (
    {
      variant = 'basic',
      icon,
      svgIcon,
      text,
      isSubMenu = false,
      isSelected,
      onClick,
      disabled = false,
      closeMenuOnClick = true,
      showSelectedIcon = false,
      textVariant = 'body',
      iconRight,
      iconRightClose: _iconRightClose,
      buttonStyle: _buttonStyle,
      useOutlinedIcon,
      iconUseSymbols,
      event,
      'aria-describedby': ariaDescribedBy,
      onBlur,
      onFocus,
      onMouseMove,
      onMouseDown,
      onPointerDown,
      onPointerEnter,
      onPointerLeave,
      onKeyDown,
      onKeyUp,
      ...props
    }: MenuButtonProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const { onClose } = useMenuContext()

    const onButtonClick = (mouseEvent: React.MouseEvent<HTMLButtonElement>) => {
      if (event) onClickEvent(event)
      onClick?.(mouseEvent)

      if (closeMenuOnClick) {
        onClose?.()
      }
    }

    return (
      <BaseMenuItem {...props}>
        <StyledMenuButton
          $variant={variant}
          $isSelected={!!isSelected}
          onClick={onButtonClick}
          title={typeof text === 'string' ? text : undefined}
          ref={ref}
          disabled={disabled}
          type="button" // In case menu button type submit is needed at some point, type prop can be added (like in button.tsx)
          onBlur={onBlur}
          onFocus={onFocus}
          onMouseMove={onMouseMove}
          onMouseDown={onMouseDown}
          onPointerDown={onPointerDown}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          aria-describedby={ariaDescribedBy}
        >
          <MenuButtonContent
            text={text}
            useOutlinedIcon={useOutlinedIcon}
            iconUseSymbols={iconUseSymbols}
            textVariant={textVariant}
            isSubMenu={isSubMenu}
            icon={icon}
            svgIcon={svgIcon}
            iconRight={showSelectedIcon && isSelected ? 'check' : iconRight}
          />
        </StyledMenuButton>
      </BaseMenuItem>
    )
  }
)
